import { useEffect, useState } from "react";
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from "@/components/golf/golf-card";
import { GolfButton } from "@/components/golf/golf-button";
import { DrillStatusBadge } from "@/components/golf/drill-status-badge";
import { Video, Target, TrendingUp, Clock, ArrowRight, User, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getLatestSnapshot } from "@/lib/api/snapshot";
import { PrimaryFaultCard } from "@/components/snapshot/PrimaryFaultCard";

export default function Home() {
  const navigate = useNavigate();
  const [latestSnapshot, setLatestSnapshot] = useState<any>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);

  useEffect(() => {
    loadLatestSnapshot();
  }, []);

  const loadLatestSnapshot = async () => {
    try {
      setLoadingSnapshot(true);
      const snapshot = await getLatestSnapshot();
      setLatestSnapshot(snapshot);
    } catch (error) {
      console.error('Error loading latest snapshot:', error);
      // Silently fail - snapshot is optional
    } finally {
      setLoadingSnapshot(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1" />
          <img 
            src="/lovable-uploads/bbc133b7-6cd1-44ba-941b-2340e4755a16.png" 
            alt="No Doubles Golf Co." 
            className="h-16"
          />
          <div className="flex-1 flex justify-end">
            <GolfButton
              variant="ghost"
              size="sm"
              onClick={() => navigate("/profile")}
              className="p-2"
            >
              <User size={20} />
            </GolfButton>
          </div>
        </div>
        <div className="text-center">
          <p className="text-muted-foreground">
            Eliminate fat shots, fix your slice, nail those short putts
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-6 mb-8">
        <div className="grid grid-cols-2 gap-4">
          {/* PLA-143: V3 Upload entry point */}
          <GolfButton
            onClick={() => navigate("/mvp/upload")}
            className="h-24 flex-col gap-2"
          >
            <Video size={24} />
            <span>Record Swing</span>
          </GolfButton>
          <GolfButton
            onClick={() => navigate("/practice")}
            variant="secondary"
            className="h-24 flex-col gap-2"
          >
            <Target size={24} />
            <span>Practice Plan</span>
          </GolfButton>
        </div>
      </div>

      {/* Primary Fault from Latest Snapshot */}
      {!loadingSnapshot && latestSnapshot && latestSnapshot.faults && latestSnapshot.faults.length > 0 && (
        <div className="px-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Your Primary Focus</h2>
            <GolfButton 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(`/snapshot/results/${latestSnapshot.snapshot.id}`)}
            >
              View Full Snapshot <ArrowRight size={16} className="ml-1" />
            </GolfButton>
          </div>
          {latestSnapshot.faults[0] && (
            <PrimaryFaultCard
              taxonomyKey={latestSnapshot.faults[0].taxonomy_key}
              displayName={latestSnapshot.faults[0].taxonomy_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              avgSeverity={latestSnapshot.faults[0].avg_severity}
              consistency={latestSnapshot.faults[0].consistency}
            />
          )}
        </div>
      )}

      {/* Snapshot CTA if no snapshot exists */}
      {!loadingSnapshot && !latestSnapshot && (
        <div className="px-6 mb-8">
          <GolfCard className="bg-gradient-to-r from-primary/5 to-success/5 border-primary/20">
            <GolfCardHeader>
              <GolfCardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Create Five-Swing Snapshot
              </GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Analyze 5 of your swings together to identify your primary fault and get a personalized training plan.
              </p>
              <GolfButton
                onClick={() => navigate("/snapshot/select")}
                className="w-full"
              >
                Create Snapshot
              </GolfButton>
            </GolfCardContent>
          </GolfCard>
        </div>
      )}

      {/* Recent Analysis */}
      <div className="px-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent Analysis</h2>
          <GolfButton 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/analyze")}
          >
            View All <ArrowRight size={16} className="ml-1" />
          </GolfButton>
        </div>
        
        <GolfCard>
          <GolfCardHeader>
            <div className="flex items-center justify-between">
              <GolfCardTitle>Driver Practice</GolfCardTitle>
              <span className="text-sm text-muted-foreground">2 hours ago</span>
            </div>
          </GolfCardHeader>
          <GolfCardContent>
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-success" />
                <span className="text-sm font-medium">Improved</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">14s swing</span>
              </div>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Keep your left arm straighter through impact
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Slow down your backswing tempo
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                Focus on weight transfer to your front foot
              </li>
            </ul>
          </GolfCardContent>
        </GolfCard>
      </div>

      {/* Active Drills */}
      <div className="px-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Active Drills</h2>
          <GolfButton 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/practice")}
          >
            View All <ArrowRight size={16} className="ml-1" />
          </GolfButton>
        </div>
        
        <div className="space-y-3">
          <GolfCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium mb-1">Alignment Stick Drill</h3>
                <p className="text-sm text-muted-foreground">Fix slice issues</p>
              </div>
              <DrillStatusBadge status="progress" />
            </div>
          </GolfCard>
          
          <GolfCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium mb-1">Slow Motion Swings</h3>
                <p className="text-sm text-muted-foreground">Tempo improvement</p>
              </div>
              <DrillStatusBadge status="new" />
            </div>
          </GolfCard>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="px-6">
        <GolfCard className="bg-gradient-to-r from-primary/5 to-success/5 border-primary/20">
          <GolfCardHeader>
            <GolfCardTitle className="text-primary">Your Progress</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">12</div>
                <div className="text-sm text-muted-foreground">Swings analyzed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-success">4</div>
                <div className="text-sm text-muted-foreground">Drills completed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-info">7</div>
                <div className="text-sm text-muted-foreground">Days streak</div>
              </div>
            </div>
          </GolfCardContent>
        </GolfCard>
      </div>
    </div>
  );
}